import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, CreditCard, ExternalLink, X } from 'lucide-react';
import { Button, IconButton } from '@/components/ui';
import { useSetupSessionOffer } from '../../hooks/useSetupSessionOffer';
import { useCardlessTrialNotice } from '../../hooks/useCardlessTrialNotice';
import { openSetupSession } from '../../constants/externalLinks';

const STRIP =
  'flex-none flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2 ' +
  'bg-gradient-to-r from-accent-700 via-accent-600 to-accent-mint-500 ' +
  'border-b border-accent-800/30';

const ICON_CHIP =
  'hidden sm:flex h-7 w-7 flex-none items-center justify-center rounded-lg ' +
  'bg-white/20 ring-1 ring-white/25';

/** Solid white button — reserved for the one primary action on the strip. */
const CTA_PRIMARY =
  'flex-none border-transparent bg-white text-accent-700 hover:bg-white/90 focus-visible:ring-white/60';

/** Translucent button — reads as an action but never outranks the primary CTA. */
const CTA_QUIET =
  'flex-none border-white/45 bg-ink-950/20 text-white hover:bg-ink-950/30 focus-visible:ring-white/60';

const PILL =
  'hidden md:inline-flex flex-none px-2 py-0.5 rounded-full bg-white/15 text-11 font-semibold text-white';

const DISMISS =
  'flex-none text-white/80 hover:bg-white/15 hover:text-white focus-visible:ring-white/60';

const trialCountdown = (daysLeft: number): string => {
  if (daysLeft === 0) return 'Trial ends today';
  if (daysLeft === 1) return 'Trial ends tomorrow';
  return `Trial ends in ${daysLeft} days`;
};

interface AccountNoticeBarViewProps {
  /** Show the free onboarding call pitch. */
  showSetup: boolean;
  /** Show the add-a-card nudge. */
  showCard: boolean;
  /** Days left in the setup offer window — only used when the offer is alone on the strip. */
  setupDaysRemaining: number | null;
  /** Days left in the trial, or null when there's no deadline to quote. */
  trialDaysLeft: number | null;
  /** Renders the lapsed-account strip instead of everything else. */
  lapsed: boolean;
  onBookSession: () => void;
  onAddCard: () => void;
  onDismiss: () => void;
}

/**
 * Presentation for the account notice strip. Split from the container so every state
 * can be rendered on its own; the container below owns the eligibility rules.
 */
const AccountNoticeBarView: React.FC<AccountNoticeBarViewProps> = ({
  showSetup,
  showCard,
  setupDaysRemaining,
  trialDaysLeft,
  lapsed,
  onBookSession,
  onAddCard,
  onDismiss,
}) => {
  // Lapsed account — nothing else matters until a card is on file.
  if (lapsed) {
    return (
      <div
        className="flex-none flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2 bg-danger-600 border-b border-danger-800/40"
        role="region"
        aria-label="Subscription inactive"
      >
        <span className={ICON_CHIP}>
          <CreditCard className="h-4 w-4 text-white" aria-hidden="true" />
        </span>
        <p className="flex-1 min-w-0 text-13 text-white/90 truncate">
          <span className="font-semibold text-white">Your trial has ended.</span>
          <span className="hidden sm:inline"> Add a payment method to reactivate your account.</span>
        </p>
        <Button
          size="sm"
          variant="secondary"
          onClick={onAddCard}
          className="flex-none border-transparent bg-white text-danger-600 hover:bg-white/90 focus-visible:ring-white/60"
        >
          Reactivate
        </Button>
      </div>
    );
  }

  if (!showSetup && !showCard) return null;

  // Card nudge alone — promote it to the primary CTA.
  if (!showSetup) {
    return (
      <div className={STRIP} role="region" aria-label="Trial status">
        <span className={ICON_CHIP}>
          <CreditCard className="h-4 w-4 text-white" aria-hidden="true" />
        </span>
        {/* Only claim a trial when we have a deadline to back it up; otherwise state the
            plain fact that no card is on file. */}
        <p className="flex-1 min-w-0 text-13 text-white/85 truncate">
          {trialDaysLeft === null ? (
            <>
              <span className="font-semibold text-white">No payment method on file</span>
              <span className="hidden sm:inline"> — add a card to keep your account active.</span>
            </>
          ) : (
            <>
              <span className="font-semibold text-white">You're on a free trial</span>
              <span className="hidden sm:inline"> — add a card to keep your account after it ends.</span>
            </>
          )}
        </p>
        {trialDaysLeft !== null && <span className={PILL}>{trialCountdown(trialDaysLeft)}</span>}
        <Button size="sm" variant="secondary" onClick={onAddCard} className={CTA_PRIMARY}>
          Add payment method
        </Button>
        <IconButton
          icon={X}
          size="sm"
          onClick={onDismiss}
          aria-label="Hide trial reminder"
          className={DISMISS}
        />
      </div>
    );
  }

  return (
    <div
      className={STRIP}
      role="region"
      aria-label={showCard ? 'Account setup and trial status' : 'Free setup session offer'}
    >
      <span className={ICON_CHIP}>
        <CalendarCheck className="h-4 w-4 text-white" aria-hidden="true" />
      </span>

      <p className="flex-1 min-w-0 text-13 text-white/85 truncate">
        <span className="font-semibold text-white">Free setup session</span>
        <span className="hidden sm:inline">
          {' '}
          — get your account dialed in on a 1:1 call with our team.
        </span>
      </p>

      {/* Only one countdown on the strip: the trial deadline wins when it applies,
          otherwise the offer window gets the pill. */}
      {!showCard && setupDaysRemaining !== null && (
        <span className={PILL}>
          {setupDaysRemaining === 1 ? '1 day left' : `${setupDaysRemaining} days left`}
        </span>
      )}

      <Button size="sm" variant="secondary" onClick={onBookSession} className={CTA_PRIMARY}>
        Book my session
        <ExternalLink className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
      </Button>

      {/* The card nudge is one chip, not another button-plus-label cluster: it carries its
          own countdown so the strip never shows two competing deadlines. */}
      {showCard && (
        <>
          <span className="hidden md:block h-6 w-px flex-none bg-white/25" aria-hidden="true" />
          <Button size="sm" variant="secondary" onClick={onAddCard} className={CTA_QUIET}>
            <CreditCard className="h-3.5 w-3.5" aria-hidden="true" />
            {trialDaysLeft !== null && (
              <span className="hidden lg:inline font-normal text-white/85">
                {trialCountdown(trialDaysLeft)} ·
              </span>
            )}
            Add a card
          </Button>
        </>
      )}

      <IconButton
        icon={X}
        size="sm"
        onClick={onDismiss}
        aria-label="Hide account notices"
        className={DISMISS}
      />
    </div>
  );
};

/**
 * Single top-of-app strip for account-level nudges. The two things we want to say to a
 * new account — book the free setup call, and add a card before the trial lapses —
 * previously shipped as two stacked gradient banners that read as duplicates and
 * showed the same countdown twice. They now share one row with a deliberate hierarchy:
 *
 *   • Both apply    → setup session is the primary (white) CTA; the card nudge sits to
 *                     the right as a quiet CTA with the trial countdown. Dismissing
 *                     clears the row, but the card nudge returns on the next load.
 *   • Card only     → the card nudge is promoted to primary.
 *   • Setup only    → the offer stands alone with its own countdown.
 *   • Trial lapsed  → a red strip replaces everything; reactivating is the only path.
 */
const AccountNoticeBar: React.FC = () => {
  const navigate = useNavigate();
  const setup = useSetupSessionOffer();
  const trial = useCardlessTrialNotice();

  const showSetup = setup.isEligible;
  const showCard = trial.state === 'trial';

  return (
    <AccountNoticeBarView
      showSetup={showSetup}
      showCard={showCard}
      setupDaysRemaining={setup.daysRemaining}
      trialDaysLeft={trial.daysLeft}
      lapsed={trial.state === 'cancelled'}
      onBookSession={openSetupSession}
      // Land on billing with the add-card modal already open — one click, not two.
      onAddCard={() => navigate('/billing', { state: { openPaymentMethods: true } })}
      onDismiss={() => {
        if (showSetup) setup.dismiss();
        if (showCard) trial.dismiss();
      }}
    />
  );
};

export default AccountNoticeBar;
